import { ElementRef, SecurityContext, Component, Input, OnInit, OnDestroy, ChangeDetectorRef, ViewChild, ViewEncapsulation } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { KeyboardKeyCodes } from 'common/common/constants/keyboard-key-codes';
import { MessageBusEvents } from 'common/common/constants/message-bus-events';
import { LinkTargetBlankFilter } from 'common/html-helper/link-target-blank';
import { WorkItemTypes } from 'common/workitems/constants/workitem-types';
import { RxEmitter } from 'common/rxjs/rx-emitter';
import { BcAnalytics } from 'common/analytics/analytics.service';
import { InnerTextPipe } from './filters/inner-text.pipe';
import { cloneDeep, defaultTo, debounce } from 'lodash';
import { VirtualScrollerComponent, IPageInfo } from 'ngx-virtual-scroller';
import { CannedMessageRequest } from 'common/canned-messages/canned-message-request.interface';
import { Subscription } from 'rxjs';
import { CannedMessage } from './canned-message.model';
import { WorkItemsTrackingService } from 'common/workitems/workitems-tracking.service';
import { WorkItemEventParams } from 'common/common/constants/analytics-enums';
import { BcCannedMessagesRepo } from 'common/canned-messages/canned-messages-repo';
import { HierarchyDropdownComponent } from 'shared/components/hierarchy-dropdown/hierarchy-dropdown.component';

export const SEARCH_QUERY_DEBOUNCE_TIME = 300;
export const SEARCH_FIELD_FOCUS_TIMEOUT = 250;

@Component({
  selector: 'app-canned-message-panel',
  templateUrl: './canned-message-panel.component.html',
  styleUrls: ['./canned-message-panel.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class CannedMessagePanelComponent implements OnInit, OnDestroy {
  @Input()
  public eventTargetId: string;
  @Input()
  public workItemId: string;
  @Input()
  public workItemType: WorkItemTypes;
  @Input()
  public cannedMessageRequest: CannedMessageRequest;
  @Input()
  public isCannedMessagesTabActive: boolean = true;
  @Input()
  get isCustomTabActive(): boolean {
    return this._isCustomTabActive;
  }
  set isCustomTabActive(value: boolean) {
    if (value !== this._isCustomTabActive) {
      this._isCustomTabActive = value;

      this._updateCNMTrackState();
    }
  }
  @Input()
  get isOpened(): boolean {
    return this._isOpened;
  }
  set isOpened(value: boolean) {
    if (value !== this._isOpened) {
      this._isOpened = value;

      this._updateCNMTrackState();
    }
  }
  @Input()
  public isExpanded: boolean;

  @ViewChild('searchField')
  public searchField: ElementRef;
  @ViewChild('virtualScrollerElement')
  public virtualScrollerElement: VirtualScrollerComponent;
  @ViewChild('hierarchyDropdown')
  public hierarchyDropdown: HierarchyDropdownComponent<CannedMessage[]>;

  public linkTargetBlankFilter;

  public searchQuery: string;
  public translations: object;
  public messages: CannedMessage[];
  public folders: CannedMessage[];
  public selectedCannedMessageFolder: CannedMessage;
  public cannedMessageFolders: CannedMessage[];
  public messagesInSelectedFolder: CannedMessage[];
  public filteredMessages: CannedMessage[];
  public virtualFilteredMessages: CannedMessage[];
  public activateCMPanelSubscription: Subscription;
  public isSearchFieldFocused: boolean;
  public dropdownError: boolean;
  public cannedMessagesAreLoading: boolean;
  public activeCannedMessageIndex: number;
  public virtualStartIndex = 0;

  private lastCustomTabInputFocusId: string = 'listPickerSearchField';
  private _isOpened = false;
  private _isCustomTabActive: boolean = false;
  private _debouncedResetActiveIndexAndFolders;

  constructor(private _bcCannedMessagesRepo: BcCannedMessagesRepo,
              private _element: ElementRef,
              private _bcAnalytics: BcAnalytics, private _sanitizer: DomSanitizer,
              private _translateService: TranslateService, private _rxEmitter: RxEmitter,
              private _innerTextPipe: InnerTextPipe, private _changeDetector: ChangeDetectorRef,
              private _workItemsTrackingService: WorkItemsTrackingService) {
    this.linkTargetBlankFilter = LinkTargetBlankFilter();

    this.selectedCannedMessageFolder = {
      id: null
    };
    this.filteredMessages = [];
    this.messagesInSelectedFolder = [];

    this.isSearchFieldFocused = false;
    this.searchQuery = '';

    this.dropdownError = false;
    this.cannedMessagesAreLoading = false;

    this._setFolderTranslations();

    this._rxEmitter.subscribe(MessageBusEvents.RequestCustomTabPanel, () => {
      this._restoreCustomTabFocus();
    });

    this._debouncedResetActiveIndexAndFolders = debounce(() => this._resetActiveIndexAndFolders(), SEARCH_QUERY_DEBOUNCE_TIME);
  }

  ngOnInit(): void {
    this.activeCannedMessageIndex = 0;
    this._getCannedMessages();

    if (!this.eventTargetId) {
      this.eventTargetId = this.workItemId;
    }

    this.activateCMPanelSubscription = this._rxEmitter.subscribe(`${MessageBusEvents.ActivateCannedMessagePanel}${this.eventTargetId}`, () => {
      this._focusSearchField();
    });

    this._updateCNMTrackState();
  }

  ngOnDestroy(): void {
    if (this.activateCMPanelSubscription) {
      this.activateCMPanelSubscription.unsubscribe();
    }
    this._workItemsTrackingService.destroyItem(this.workItemId);
  }

  public cannedMessageVirtualListUpdated(newItems: CannedMessage[]): void {
    this.virtualFilteredMessages = newItems;
  }

  public virtualStartIndexChanged(changeEvent: IPageInfo): void {
    this.virtualStartIndex = changeEvent.startIndexWithBuffer;
    this._changeDetector.detectChanges();
  }

  public searchQueryChanged(newQuery): void {
    if (newQuery !== this.searchQuery) {
      this.searchQuery = newQuery;
      this._debouncedResetActiveIndexAndFolders();
    }
  }

  public shouldDisableDropdown(): boolean {
    return !this.cannedMessageFolders || this.cannedMessageFolders.length < 2;
  }

  public setActiveIndex(idx: number): void {
    this.activeCannedMessageIndex = idx;
  }

  public onSearchFieldFocus(): void {
    this.isSearchFieldFocused = true;
  }

  public onSearchFieldBlur(): void {
    this.isSearchFieldFocused = false;
  }

  public handleKeyDown(event: KeyboardEvent): void {
    switch (event.which) {
      case KeyboardKeyCodes.ESC:
        if (this.isSearchFieldFocused) {
          this.closePanel();
        }
        break;

      case KeyboardKeyCodes.UP:
        event.preventDefault();
        event.stopPropagation();
        this.activeCannedMessageIndex--;
        this._clampActiveIndex(false);
        break;

      case KeyboardKeyCodes.DOWN:
        event.preventDefault();
        event.stopPropagation();
        this.activeCannedMessageIndex++;
        this._clampActiveIndex(true);
        break;

      case KeyboardKeyCodes.ENTER:
        event.preventDefault();
        event.stopPropagation();
        this._bcAnalytics.eventTrack('CannedMessageSelected', { from: 'Keyboard' });
        this._selectCannedMessage(this.filteredMessages[this.activeCannedMessageIndex]);
        break;

      case KeyboardKeyCodes.TAB:
        event.preventDefault();
        event.stopPropagation();
        if (!this.searchQuery) {
          this.hierarchyDropdown.trigger.focus();
        }
        break;

      default:
        event.stopPropagation();
        break;
    }
    this._changeDetector.detectChanges();
  }

  public isMessagesEmpty(): boolean {
    return (!this.messages || this.messages.length === 0 ||
      (this.messages.length === 1 && this.messages[0].isRoot));
  }

  public cannedMessageClick(cannedMessage: CannedMessage): void {
    this._bcAnalytics.eventTrack('CannedMessageSelected', { from: 'MouseClick' });
    this._selectCannedMessage(cannedMessage);
  }

  public cannedMessageFolderChange(folder: HTMLOptionElement): void {
    if (folder && this.messages) {
      this._selectCannedMessageFolder(this.folders[folder.value]);
      this._focusSearchField();
    }
  }

  public closePanel(): void {
    this._rxEmitter.send(`${MessageBusEvents.CloseCannedMessagePanel}${this.eventTargetId}`, null);
  }

  public clearSearchQuery(): void {
    this.searchQuery = '';
    this._setCannedMessageFolders();
  }

  public isImage(msg: CannedMessage): boolean {
    return msg.type && msg.type.indexOf('image') === 0;
  }

  public focusChanged(event: HTMLElement): void {
    this.lastCustomTabInputFocusId = event && event.id;
  }

  public onDropdownToggle(status): void {
    this._rxEmitter.send(`${MessageBusEvents.ToggleCannedMessageFolderDropdown}${this.eventTargetId}`, status);
  }

  private _innerTextCompare(actual: CannedMessage, expected: string): boolean {
    if (actual && typeof actual === 'object') {
      // Check only the relevant properties of a canned message object
      return _checkForTextInHtml(actual.name, expected) ||
        _checkForTextInHtml(actual.text, expected) ||
        _checkForTextInHtml(actual.keywords, expected) ||
        _checkForTextInHtml(actual.slashCommand, expected);
    }

    return false;

    function _checkForTextInHtml(htmlStr: string, searchText: string): boolean {
      if (!searchText) {
        return true;
      }

      if (!htmlStr) {
        return false;
      }

      const textStr = htmlStr.replace(/(<[^>]*>)/gi, '').toLocaleLowerCase();
      return textStr.indexOf(searchText.toLocaleLowerCase()) !== -1;
    }
  }

  private _updateCNMTrackState(): void {
    this._workItemsTrackingService.updateDataByItemId(this.workItemId, {
      [WorkItemEventParams.CANNED_MESSAGES_PANEL_VISIBLE]: this.isOpened,
      [WorkItemEventParams.CUSTOM_TAB_VISIBLE]: this.isOpened && this.isCustomTabActive
    });
  }

  private _setFolderTranslations(): void {
    this.translations = this._translateService.instant([
      'bcCannedMessagePanel.allFolders',
      'bcCannedMessagePanel.defaultFolderName',
      'bcCannedMessagePanel.emptyDropdown'
    ]);
  }

  private _setCannedMessageFolders(): void {
    let cannedMessageFolders;
    let currentFolder = this.selectedCannedMessageFolder;

    if (this.searchQuery) {
      currentFolder = {
        id: null
      };
      cannedMessageFolders = [{ value: 0, name: this.translations['bcCannedMessagePanel.allFolders'], id: null, parentId: undefined }];
    } else if (!this.folders || !this.folders.length) {
      cannedMessageFolders = [{ value: 0, name: this.translations['bcCannedMessagePanel.emptyDropdown'], id: null }];
    } else {
      cannedMessageFolders = this.folders.map((folder, index) => {

        return {
          id: folder.id ? folder.id : null,
          name: folder.isRoot ? this.translations['bcCannedMessagePanel.defaultFolderName'] : folder.name,
          parentId: folder.parentId,
          value: index
        };
      });
    }

    this.cannedMessageFolders = cannedMessageFolders;
    this._selectCannedMessageFolder(currentFolder);
  }

  private _setFilteredMessages(): void {
    if (this.searchQuery) {
      this.filteredMessages = this.messages.filter((message) => {
        return this._innerTextCompare(message, this.searchQuery);
      });
    } else {
      this.filteredMessages = this.messagesInSelectedFolder;
    }
    this.filteredMessages = this.filteredMessages.filter((message) => {
      return message.name && !message.isFolder && (!message.ChatType || message.ChatType === this.workItemType);
    });
  }

  private _focusSearchField(): void {
    if (this.searchField) {
      setTimeout(() => this.searchField.nativeElement.focus(), SEARCH_FIELD_FOCUS_TIMEOUT);
    }
  }

  private _resetActiveIndexAndFolders(): void {
    this._resetActiveIndex();
    this._setCannedMessageFolders();
    this._setFilteredMessages();
  }

  private _resetActiveIndex(): void {
    this.activeCannedMessageIndex = 0;
    this._clampActiveIndex(false);
    if (this.virtualScrollerElement) {
      this.virtualScrollerElement.scrollToIndex(this.activeCannedMessageIndex, true, 0, 0);
    }
    this._changeDetector.detectChanges();
  }

  private _clampActiveIndex(isIncremented: boolean): void {
    const newVal = this.activeCannedMessageIndex;
    // Clamp value change to list length
    if (newVal < 0 || this.filteredMessages.length === 0) {
      this.activeCannedMessageIndex = 0;
    } else if (newVal >= this.filteredMessages.length) {
      this.activeCannedMessageIndex = this.filteredMessages.length - 1;
    }

    const scrollToTop = !isIncremented;
    this._scrollActiveCannedMessageIntoView(scrollToTop);
  }

  private _scrollActiveCannedMessageIntoView(scrollToTop: boolean): void {
    const messageElements = this._element.nativeElement.querySelectorAll('.bc-canned-message-wrapper');
    if (!messageElements.length) {
      return;
    }

    const currentElement = this._findCurrentElementByIdIndex(messageElements);
    if (!currentElement) {
      return;
    }

    const currentElementClientRect = currentElement.getBoundingClientRect();
    const currentElementTop = currentElementClientRect.top;
    const currentElementBottom = currentElementTop + currentElementClientRect.height;

    const scrollElement = this._getScrollContainer();
    const scrollElementClientRect = scrollElement.getBoundingClientRect();
    const scrollElementTop = scrollElementClientRect.top;
    const scrollElementBottom = scrollElementTop + scrollElementClientRect.height;

    if (scrollToTop && currentElementTop < scrollElementTop) {
      currentElement.scrollIntoView();
    } else if (!scrollToTop && currentElementBottom > scrollElementBottom) {
        currentElement.scrollIntoView(false);
    }
  }

  private _findCurrentElementByIdIndex(cannedMessageElements: HTMLElement[]): HTMLElement {
    let currentElement = null;
    const cmListLength = cannedMessageElements.length;
    for (let i = 0; i < cmListLength; i++) {
      if (cannedMessageElements[i].getAttribute('data-id') === this.activeCannedMessageIndex.toString()) {
        currentElement = cannedMessageElements[i];
        break;
      }
    }

    return currentElement;
  }

  private _getCannedMessages(): void {
    this.cannedMessagesAreLoading = true;
    this._bcCannedMessagesRepo.getCannedMessages(this.cannedMessageRequest)
      .then((response) => {
        const folders: CannedMessage[] = cloneDeep(response.folders.filter(f => defaultTo(f.isVisible, true)));
        const clonedMessages: CannedMessage[] = cloneDeep(response.messages.filter(m => defaultTo(m.isVisible, true)));
        const messages: CannedMessage[] = clonedMessages.sort((a, b) => a.name.toLocaleLowerCase().localeCompare(b.name.toLocaleLowerCase()));

        if (this._hasMessageWithoutFolder(messages)) {
          // adding root folder
          folders.unshift({
            folderName: 'ignored',
            isFolder: true,
            isRoot: true,
            id: null
          });
        }

        // Catch sanitize errors with mXSS
        messages.map(message => this._formatMessage(message));

        this.messages = messages;
        this.folders = folders;
        this.dropdownError = false;

        this._setCannedMessageFolders();

        let selectedFolder: CannedMessage = { id: null };

        if (this.selectedCannedMessageFolder && this.selectedCannedMessageFolder.id) {
          selectedFolder = this.selectedCannedMessageFolder;
        } else if (folders && folders.length) {
          selectedFolder = folders[0];
        }

        this._selectCannedMessageFolder(selectedFolder);
        this.cannedMessagesAreLoading = false;

        this._changeDetector.detectChanges();
      })
      .catch(() => {
        this.dropdownError = true;
        this.cannedMessagesAreLoading = false;
        this._changeDetector.detectChanges();
      });
  }

  private _hasMessageWithoutFolder(messages: CannedMessage[]): boolean {
    return messages.some(message => !message.folderId);
  }

  private _formatMessage(message: CannedMessage): CannedMessage {
    message.text = this._makeLinkOpenNewTab(message.text);
    return this._sanitizeMessage(message);
  }

  private _makeLinkOpenNewTab(messageText: string): string {
    return this.linkTargetBlankFilter(messageText);
  }

  private _sanitizeMessage(message: CannedMessage): CannedMessage {
    message.name = this._sanitizeText(message.name);
    message.text = this._stripPushTags(message.text);
    message.text = this._sanitizeText(message.text);

    return message;
  }

  private _sanitizeText(text: string): string {
    let sanitizedText;

    try {
      sanitizedText = this._sanitizer.sanitize(SecurityContext.HTML, text);
    } catch (e) {
      // Couldn't sanitize html in body, fallback to text-only
      sanitizedText = this._innerTextPipe.transform(text);
    }

    return sanitizedText;
  }

  private _stripPushTags(text: string): string {
    return text.replace(/\[?<push>/ig, '[')
      .replace(/<\/push>]?/ig, ']');
  }

  private _selectCannedMessage(cannedMessage: CannedMessage): void {
    if (cannedMessage) {
      this.clearSearchQuery();
      const eventArgs = { ...cannedMessage, eventSource: CannedMessagePanelComponent.name };
      setTimeout(() => this._rxEmitter.send(`${MessageBusEvents.SendCannedMessageToEditor}${this.eventTargetId}`, eventArgs), 0);
    }
    this.activeCannedMessageIndex = 0;
    this._getScroller().scrollTop = 0;
  }

  private _getScrollContainer(): HTMLElement {
    return this._element.nativeElement.querySelector('.virtual-scroll-container');
  }

  private _getScroller(): HTMLElement {
    return this._element.nativeElement.querySelector('.virtual-scroll-container .scrollable-content');
  }

  private _selectCannedMessageFolder(folder: CannedMessage): void {
    this.selectedCannedMessageFolder = folder;
    this._resetActiveIndex();
    this.messagesInSelectedFolder = this.messages.filter(value => value.folderId === folder.id);
    this._setFilteredMessages();
  }

  private _restoreCustomTabFocus(): void {
    const elementId = this.lastCustomTabInputFocusId;
    if (elementId) {
      const element = document.getElementById(elementId);
      if (element) {
        setTimeout(() => {
          // Does not show cursor without the timeout.
          element.focus();
        }, 50);
      }
    }
  }
}
